import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

interface InsertTableGridProps {
    onInsert: (rows: number, columns: number) => void;
}

const GRID_SIZE = 8;
const MAX_TABLE_SIZE = 50;
const DEFAULT_CUSTOM_SIZE = "3";

// Visual grid for picking table dimensions, similar to Word/Google Docs "Insert table" controls.
const InsertTableGrid: FC<InsertTableGridProps> = ({ onInsert }) => {
    const { t } = useTranslation();
    const [hovered, setHovered] = useState<{ row: number; col: number } | null>(null);
    const [isCustomSize, setIsCustomSize] = useState(false);
    const [customRows, setCustomRows] = useState(DEFAULT_CUSTOM_SIZE);
    const [customColumns, setCustomColumns] = useState(DEFAULT_CUSTOM_SIZE);
    const [customSizeError, setCustomSizeError] = useState("");

    const handleCustomSizeSubmit = () => {
        const rows = parseInt(customRows, 10);
        const columns = parseInt(customColumns, 10);
        const isValid =
            Number.isInteger(rows) &&
            Number.isInteger(columns) &&
            rows >= 1 &&
            columns >= 1 &&
            rows <= MAX_TABLE_SIZE &&
            columns <= MAX_TABLE_SIZE;

        if (!isValid) {
            setCustomSizeError(t("richTextEditor.invalidSize", { max: MAX_TABLE_SIZE }));
            return;
        }

        onInsert(rows, columns);
    };

    if (isCustomSize) {
        return (
            <div className="flex flex-col gap-2 w-[220px]">
                <span className="text-sm font-medium">{t("richTextEditor.customSizeTitle")}</span>
                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        min={1}
                        max={MAX_TABLE_SIZE}
                        value={customRows}
                        onChange={(e) => setCustomRows(e.target.value)}
                        aria-label={t("richTextEditor.rows")}
                        className="w-16 border border-light-border-100 dark:border-dark-border-100 rounded px-2 py-1 text-sm bg-transparent"
                    />
                    <span>×</span>
                    <input
                        type="number"
                        min={1}
                        max={MAX_TABLE_SIZE}
                        value={customColumns}
                        onChange={(e) => setCustomColumns(e.target.value)}
                        aria-label={t("richTextEditor.columns")}
                        className="w-16 border border-light-border-100 dark:border-dark-border-100 rounded px-2 py-1 text-sm bg-transparent"
                    />
                </div>
                {customSizeError && <span className="text-xs text-red-500">{customSizeError}</span>}
                <div className="flex justify-end gap-2 mt-1">
                    <button
                        type="button"
                        onClick={() => setIsCustomSize(false)}
                        className="px-3 py-1 text-sm rounded hover:bg-gray-100 dark:hover:bg-dark-bg-700"
                    >
                        {t("richTextEditor.cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={handleCustomSizeSubmit}
                        className="px-3 py-1 text-sm rounded bg-light-bg-300 dark:bg-dark-bg-400 text-white"
                    >
                        {t("richTextEditor.insert")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="text-sm font-medium text-center">
                {hovered ? `${hovered.row + 1} × ${hovered.col + 1}` : t("richTextEditor.insertTable")}
            </div>
            <div
                className="grid gap-[3px]"
                style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 18px)` }}
                onMouseLeave={() => setHovered(null)}
            >
                {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, index) => {
                    const row = Math.floor(index / GRID_SIZE);
                    const col = index % GRID_SIZE;
                    const isActive = hovered != null && row <= hovered.row && col <= hovered.col;

                    return (
                        <div
                            key={`${row}-${col}`}
                            onMouseEnter={() => setHovered({ row, col })}
                            onClick={() => onInsert(row + 1, col + 1)}
                            className={`w-[18px] h-[18px] border cursor-pointer ${
                                isActive
                                    ? "bg-light-bg-300 dark:bg-dark-bg-400 border-light-bg-300 dark:border-dark-bg-400"
                                    : "bg-gray-50 dark:bg-dark-bg-800 border-gray-300 dark:border-dark-border-200"
                            }`}
                        />
                    );
                })}
            </div>
            <button
                type="button"
                onClick={() => setIsCustomSize(true)}
                className="text-sm text-light-bg-300 dark:text-dark-text-100 hover:underline self-center mt-1"
            >
                {t("richTextEditor.customSize")}
            </button>
        </div>
    );
};

export default InsertTableGrid;
